import { EPA608_COPY } from './copy/epa608';
import { AIM_COPY } from './copy/aim';

// Display config for the redesigned Compliance Center landing page (AB#17116).
// Fixed bar order, display names, scope labels and colors come from the approved
// design ("Compliance Center [Final]"); the API returns counts keyed by `key`
// (/api/Asset/ComplianceCenter/Summary) and the UI owns everything else.
export const REGULATIONS = [
    { key: 'epa608', name: 'EPA Section 608', scopeLabel: 'Federal', color: '#00897B' },
    { key: 'aim', name: 'EPA AIM Act', scopeLabel: 'Federal', color: '#388E3C' },
    { key: 'carb', name: 'CA CARB RMP', scopeLabel: 'State (CA)', color: '#F57C00' },
    { key: 'wa', name: 'WA Ecology', scopeLabel: 'State (WA)', color: '#1976D2' },
    { key: 'ny', name: 'NY DEC Part 494', scopeLabel: 'State (NY)', color: '#7B1FA2' },
];

// Register-only regulations: in-scope assets are counted, but we measure no
// obligations against them, so they render as cards without a compliance split.
// Fixed sort: rule-defines-nothing first, then not-yet-assessed.
export const UNMEASURED_REGULATIONS = [
    { key: 'nj', name: 'NJ DEP', reason: 'Registration and reporting only', category: 'State (NJ)', color: '#C2185B' },
    { key: 'scaqmd', name: 'CA SCAQMD', reason: 'Not yet assessed', category: 'Regional (CA — South Coast)', color: '#9E9E9E' },
];

// AB#17128: Table and Field Spec §9 tile order, identical on every regulation page (undefined surfaces render disabled, never hidden)
export const SURFACE_ORDER = ['inScope', 'leakRateTrigger', 'dueForInspection', 'leakRepairDeadline', 'aldCalibration', 'chronicallyLeaking'];

export const SURFACE_NAMES = {
    inScope: 'In-Scope Assets',
    leakRateTrigger: 'Leak Rate Trigger Exceeded',
    dueForInspection: 'Assets Due for Inspection',
    leakRepairDeadline: 'Leak Repair Deadline Exceeded',
    aldCalibration: 'ALD Calibration Missing',
    chronicallyLeaking: 'Chronically Leaking Systems',
};

// AB#17128: regulation drill-down pages; a regulation without an entry keeps a display-only bar
export const DRILL_DOWNS = {
    epa608: {
        key: 'epa608',
        name: 'EPA Section 608',
        citation: '40 CFR Part 82, Subpart F',
        copy: EPA608_COPY,
        // Filter choices for select columns; labels must match the server catalog (ComplianceCatalog.cs)
        valueOptions: { calcMethod: ['Annualizing', 'Rolling avg'], requiredCadence: ['Quarterly', 'Annual'] },
        surfaces: {
            inScope: { name: 'In-Scope Assets', citation: '40 CFR Part 82, Subpart F', kind: 'inScope' },
            leakRateTrigger: { name: 'Leak Rate Trigger Exceeded', citation: '§ 82.157(c)', kind: 'leakRate', supportsLow: true },
            dueForInspection: { name: 'Assets Due for Inspection', citation: '§ 82.157(g)', kind: 'inspection' },
            leakRepairDeadline: { name: 'Leak Repair Deadline Exceeded', citation: '§ 82.157(d)', kind: 'repair' },
            aldCalibration: { name: 'ALD Calibration Missing', citation: '§ 82.157(g)(4)', kind: 'aldCalibration', deviceBased: true },
            chronicallyLeaking: { name: 'Chronically Leaking Systems', citation: '§ 82.157(j)', kind: 'chronicLeak', supportsLow: true },
        },
    },
    // AB#17135: EPA AIM Act — a configuration of the same page. § 84.106 citations, 15 lb floor;
    // thresholds and valueOptions are identical to EPA 608 (AC6 thresholds 20/30/10).
    aim: {
        key: 'aim',
        name: 'EPA AIM Act',
        citation: '40 CFR Part 84 · § 84.106',
        copy: AIM_COPY,
        valueOptions: { calcMethod: ['Annualizing', 'Rolling avg'], requiredCadence: ['Quarterly', 'Annual'] },
        surfaces: {
            inScope: { name: 'In-Scope Assets', citation: '40 CFR Part 84 · § 84.106', kind: 'inScope' },
            leakRateTrigger: { name: 'Leak Rate Trigger Exceeded', citation: '§ 84.106(c)', kind: 'leakRate', supportsLow: true },
            dueForInspection: { name: 'Assets Due for Inspection', citation: '§ 84.106(g)', kind: 'inspection' },
            leakRepairDeadline: { name: 'Leak Repair Deadline Exceeded', citation: '§ 84.106(d)', kind: 'repair' },
            aldCalibration: { name: 'ALD Calibration Missing', citation: '§ 84.106(g)(4)', kind: 'aldCalibration', deviceBased: true },
            chronicallyLeaking: { name: 'Chronically Leaking Systems', citation: '§ 84.106(j)', kind: 'chronicLeak', supportsLow: true },
        },
    },
};

// Own-property lookup so a hand-typed slug like "constructor" redirects instead of crashing
export const findDrillDown = (slug) => {
    const key = String(slug || '').toLowerCase();
    return Object.prototype.hasOwnProperty.call(DRILL_DOWNS, key) ? DRILL_DOWNS[key] : null;
};

// The design's legend swatch: an even gradient of all regulation colors.
export const LEGEND_GRADIENT =
    'linear-gradient(135deg, #00897B 0%, #00897B 16.66%, #388E3C 16.66%, #388E3C 33.33%, ' +
    '#F57C00 33.33%, #F57C00 50%, #1976D2 50%, #1976D2 66.66%, #C2185B 66.66%, #C2185B 83.33%, ' +
    '#7B1FA2 83.33%, #7B1FA2 100%)';

// The design renders the needs-attention segment as the regulation color mixed
// 22% into white (CSS color-mix); precomputed here so every browser renders it.
export const mixWithWhite = (hex, ratio) => {
    const n = parseInt(hex.slice(1), 16);
    const mix = (c) => Math.round(c * ratio + 255 * (1 - ratio));
    const r = mix((n >> 16) & 255);
    const g = mix((n >> 8) & 255);
    const b = mix(n & 255);
    // eslint-disable-next-line no-bitwise
    return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
};
